import { fetchAuthSession, signOut } from 'aws-amplify/auth';

const API_URL = process.env.REACT_APP_API_URL;
const STREAM_URL = process.env.REACT_APP_STREAM_URL;
// Public (unauthenticated) endpoints live under /public, a sibling of /api
// on the same API Gateway stage.
const PUBLIC_API_URL = API_URL ? API_URL.replace(/\/api$/, '/public') : '';

export async function getAuthHeaders() {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'X-Timezone': tz
    };
  } catch {
    return { 'Content-Type': 'application/json', 'X-Timezone': tz };
  }
}

async function handleResponse(res) {
  if (res.status === 401) {
    try { await signOut(); } catch { /* ignore */ }
    window.location.reload();
    throw new Error('Session expired');
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    if (body.error) return body;
    throw new Error(body.error || `Request failed (${res.status})`);
  }
  return res.json();
}

export async function getUserState() {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/state`, { headers });
  return handleResponse(res);
}

export async function sendMessage(messages, sessionId) {
  const headers = await getAuthHeaders();
  const body = { messages };
  if (sessionId) body.session_id = sessionId;
  const res = await fetch(`${API_URL}/chat`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  });
  return handleResponse(res);
}

export async function sendMessageStream(messages, sessionId, onEvent) {
  if (!STREAM_URL) throw new Error('Streaming not configured');
  const headers = await getAuthHeaders();
  const body = { messages };
  if (sessionId) body.session_id = sessionId;

  const res = await fetch(STREAM_URL, {
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  });

  if (res.status === 401) {
    try { await signOut(); } catch { /* ignore */ }
    window.location.reload();
    throw new Error('Session expired');
  }

  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    throw new Error(errBody.error || `Stream request failed (${res.status})`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    // Keep incomplete last line in buffer
    buffer = lines.pop();

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed || !trimmed.startsWith('data: ')) continue;
      try {
        const data = JSON.parse(trimmed.slice(6));
        onEvent(data);
      } catch {
        // Skip malformed SSE lines
      }
    }
  }

  // Process any remaining buffer
  if (buffer.trim().startsWith('data: ')) {
    try {
      const data = JSON.parse(buffer.trim().slice(6));
      onEvent(data);
    } catch {
      // ignore
    }
  }
}

/**
 * Voice STT — POST raw audio bytes, get back a transcript.
 * Audio is whatever MediaRecorder produced (typically audio/webm on Chrome).
 */
export async function transcribeAudio(audioBlob) {
  if (!STREAM_URL) throw new Error('Voice not configured');
  const session = await fetchAuthSession().catch(() => null);
  const token = session?.tokens?.idToken?.toString();
  const baseUrl = STREAM_URL.replace(/\/$/, '');

  const res = await fetch(`${baseUrl}/voice/transcribe`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': audioBlob.type || 'application/octet-stream',
      'X-Audio-Mime': audioBlob.type || 'audio/webm',
    },
    body: audioBlob,
  });

  if (res.status === 401) {
    try { await signOut(); } catch { /* ignore */ }
    window.location.reload();
    throw new Error('Session expired');
  }

  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    throw new Error(errBody.error || `Transcribe failed (${res.status})`);
  }

  return res.json(); // {text, language_code, language_probability}
}

/**
 * Voice TTS — POST text, get a streaming MP3 Response.
 * Caller is responsible for piping res.body into a player.
 * Returns the raw Response so callers can read headers + body.
 *
 * `previousText` — optional prior sentence; ElevenLabs uses it for prosody
 * continuity so sentence-chunk TTS flows as one utterance.
 */
export async function speakStream(text, voiceId, previousText) {
  if (!STREAM_URL) throw new Error('Voice not configured');
  const headers = await getAuthHeaders();
  const baseUrl = STREAM_URL.replace(/\/$/, '');
  const body = { text };
  if (voiceId) body.voice_id = voiceId;
  if (previousText) body.previous_text = previousText;

  const res = await fetch(`${baseUrl}/voice/speak`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  if (res.status === 401) {
    try { await signOut(); } catch { /* ignore */ }
    window.location.reload();
    throw new Error('Session expired');
  }

  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    throw new Error(errBody.error || `TTS request failed (${res.status})`);
  }

  return res;
}

/**
 * Update the user's preferred ElevenLabs voice_id.
 * Server validates against its whitelist and enforces tier gating.
 */
export async function setVoicePreference(voiceId) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/user/voice`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ voice_id: voiceId }),
  });
  return handleResponse(res);
}

/**
 * Persist the user's voice mode default (on/off) so the choice carries
 * across sessions and surfaces. Fire-and-forget — UI doesn't block on this.
 */
export async function setVoiceDefaultOn(enabled) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/user/voice`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ voice_default_on: !!enabled }),
  });
  return handleResponse(res);
}

/**
 * Update the user's Navigator push intensity.
 * level must be one of: 'coach' | 'mentor' | 'sparring_partner'.
 */
export async function setDriveLevel(level) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/user/drive-level`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ drive_level: level }),
  });
  return handleResponse(res);
}

/**
 * Dismiss the post-onboarding "tune the intensity" banner without picking
 * a new level. Marks drive_level_calibrated_at on the server.
 */
export async function acknowledgeDriveLevel() {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/user/drive-level/acknowledge`, {
    method: 'POST',
    headers,
  });
  return handleResponse(res);
}

/**
 * Set or clear the user's self-identified MBTI type. Pass null to clear.
 * Server validates against the 16 standard types.
 */
export async function setMbtiType(type) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/user/mbti`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ mbti_type: type }),
  });
  return handleResponse(res);
}

export async function getOpenSession(threadId) {
  const headers = await getAuthHeaders();
  const url = threadId
    ? `${API_URL}/sessions/open?thread_id=${threadId}`
    : `${API_URL}/sessions/open`;
  const res = await fetch(url, { headers });
  return handleResponse(res);
}

export async function startSession(threadId, entryStateScores) {
  const headers = await getAuthHeaders();
  const body = {};
  if (threadId) body.thread_id = threadId;
  if (entryStateScores) body.entry_state_scores = entryStateScores;
  const res = await fetch(`${API_URL}/sessions`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  });
  return handleResponse(res);
}

export async function listSessions(threadId) {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams();
  if (threadId) params.set('thread_id', threadId);
  const qs = params.toString();
  const url = qs ? `${API_URL}/sessions?${qs}` : `${API_URL}/sessions`;
  const res = await fetch(url, { headers });
  return handleResponse(res);
}

export async function closeSession(sessionId, data) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/sessions/${sessionId}/close`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data)
  });
  return handleResponse(res);
}

export async function getAssignments(status, threadId) {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (threadId) params.set('thread_id', threadId);
  const qs = params.toString();
  const url = qs ? `${API_URL}/assignments?${qs}` : `${API_URL}/assignments`;
  const res = await fetch(url, { headers });
  return handleResponse(res);
}

export async function submitAssignment(assignmentId, data) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/assignments/${assignmentId}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(data)
  });
  return handleResponse(res);
}

export async function saveIntake(data) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/intake`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data)
  });
  return handleResponse(res);
}

// Thread API functions

export async function getThreads() {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/threads`, { headers });
  return handleResponse(res);
}

export async function createThread(data) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/threads`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data)
  });
  return handleResponse(res);
}

export async function updateThread(threadId, data) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/threads/${threadId}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(data)
  });
  return handleResponse(res);
}

export async function deleteThread(threadId) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/threads/${threadId}`, {
    method: 'DELETE',
    headers
  });
  return handleResponse(res);
}

export async function clearThreadHistory(threadId) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/threads/${threadId}/clear`, {
    method: 'POST',
    headers
  });
  return handleResponse(res);
}

export async function getHolisticView() {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/threads/holistic`, { headers });
  return handleResponse(res);
}

// Subscription API functions

export async function getSubscriptionStatus() {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/subscription`, { headers });
  return handleResponse(res);
}

// Founding-member offer state. Public (no auth) so the landing page and pricing
// table can show launch-window pricing before sign-in.
export async function getOffer() {
  const res = await fetch(`${PUBLIC_API_URL}/offer`);
  return handleResponse(res);
}

export async function createCheckoutSession(tier, interval = 'monthly') {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/subscription/checkout`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ tier, interval })
  });
  return handleResponse(res);
}

export async function createCustomerPortal() {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/subscription/portal`, {
    method: 'POST',
    headers
  });
  return handleResponse(res);
}

export async function cancelSubscription() {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/subscription/cancel`, {
    method: 'POST',
    headers
  });
  return handleResponse(res);
}

export async function deleteAccount(confirmEmail) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/account/delete`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ confirm_email: confirmEmail })
  });
  return handleResponse(res);
}

// Notification preferences

export async function getNotificationPreferences() {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/notifications/preferences`, { headers });
  return handleResponse(res);
}

export async function updateNotificationPreferences(preferences) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/notifications/preferences`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ preferences })
  });
  return handleResponse(res);
}

// Export functions

export async function exportProgressPdf(threadId) {
  const headers = await getAuthHeaders();
  headers['Accept'] = 'application/pdf';
  const params = threadId ? `?thread_id=${threadId}` : '';
  const res = await fetch(`${API_URL}/export/progress-pdf${params}`, { headers });

  if (res.status === 403) {
    return res.json();
  }

  if (!res.ok) {
    throw new Error('Failed to export PDF');
  }

  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : `navigator-progress-${new Date().toISOString().slice(0, 10)}.pdf`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return { success: true };
}

export async function submitSuggestion(title, description) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/suggestions`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ title, description })
  });
  return handleResponse(res);
}

// Space API functions

export async function getSpaces() {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/spaces`, { headers });
  return handleResponse(res);
}

export async function createSpace(data) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/spaces`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data)
  });
  return handleResponse(res);
}

export async function updateSpace(spaceId, data) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/spaces/${spaceId}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(data)
  });
  return handleResponse(res);
}

export async function deleteSpace(spaceId) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/spaces/${spaceId}`, {
    method: 'DELETE',
    headers
  });
  return handleResponse(res);
}

// Accountability Partner

export async function getAccountability() {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/accountability`, { headers });
  return handleResponse(res);
}

export async function setAccountability(data) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/accountability`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(data)
  });
  return handleResponse(res);
}

export async function getRingShare() {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/account/ring-share`, { headers });
  return handleResponse(res);
}

export async function setRingShare(enabled) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/account/ring-share`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ enabled })
  });
  return handleResponse(res);
}

export async function removeAccountability() {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/accountability`, {
    method: 'DELETE',
    headers
  });
  return handleResponse(res);
}

// Goal Arcs

export async function getArcs(threadId) {
  const headers = await getAuthHeaders();
  const url = threadId ? `${API_URL}/threads/${threadId}/arcs` : `${API_URL}/arcs`;
  const res = await fetch(url, { headers });
  return handleResponse(res);
}

export async function updateArc(arcId, data) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/arcs/${arcId}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(data)
  });
  return handleResponse(res);
}

// Onboarding

export async function startOnboarding() {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/daily/onboarding`, {
    method: 'POST',
    headers
  });
  return handleResponse(res);
}

export async function startEntrepreneurOnboarding() {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/daily/entrepreneur-onboarding`, {
    method: 'POST',
    headers
  });
  return handleResponse(res);
}

// Daily Planning API functions

export async function getDailyPlan() {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/daily/plan`, { headers });
  return handleResponse(res);
}

export async function startPlanning() {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/daily/plan`, {
    method: 'POST',
    headers
  });
  return handleResponse(res);
}

export async function startCheckIn() {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/daily/check-in`, {
    method: 'POST',
    headers
  });
  return handleResponse(res);
}

export async function endCheckIn(sessionId) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/daily/check-in/close`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ session_id: sessionId })
  });
  return handleResponse(res);
}

export async function startReview() {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/daily/plan/review`, {
    method: 'POST',
    headers
  });
  return handleResponse(res);
}

export async function getDailyPlanHistory(days = 7) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/daily/plan/history?days=${days}`, { headers });
  return handleResponse(res);
}

export async function updateDailyAction(actionId, data) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/daily/actions/${actionId}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(data)
  });
  return handleResponse(res);
}

export async function addDailyAction(data) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/daily/actions`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data)
  });
  return handleResponse(res);
}

export async function deleteDailyAction(actionId) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/daily/actions/${actionId}`, {
    method: 'DELETE',
    headers
  });
  return handleResponse(res);
}

export async function getDailyStreak() {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/daily/streak`, { headers });
  return handleResponse(res);
}

export async function updateDailySettings(data) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/daily/settings`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(data)
  });
  return handleResponse(res);
}

export async function exportAssignmentsIcs(threadId, status) {
  const headers = await getAuthHeaders();
  headers['Accept'] = 'text/calendar';
  const params = new URLSearchParams();
  if (threadId) params.set('thread_id', threadId);
  if (status) params.set('status', status);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await fetch(`${API_URL}/export/assignments.ics${qs}`, { headers });

  if (res.status === 403) {
    return res.json();
  }

  if (!res.ok) {
    throw new Error('Failed to export calendar');
  }

  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : `navigator-tasks-${new Date().toISOString().slice(0, 10)}.ics`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return { success: true };
}

// --- Public newsletter (no auth) ---

export async function subscribeNewsletter(email, source = 'landing', website = '') {
  const res = await fetch(`${PUBLIC_API_URL}/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // `website` is the honeypot field — empty for real users, filled by bots.
    body: JSON.stringify({ email, source, website })
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || 'Subscription failed');
  return body;
}

// Deep Rest (guided rest + rehearsal audio) API functions

export async function listRestSessions({ limit = 6, source, threadId, kind } = {}) {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams({ limit: String(limit) });
  if (source) params.set('source', source);
  if (threadId) params.set('thread_id', threadId);
  if (kind) params.set('kind', kind);
  const res = await fetch(`${API_URL}/rest?${params.toString()}`, { headers });
  return handleResponse(res);
}

export async function createRestSession(opts = {}) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/rest`, {
    method: 'POST',
    headers,
    body: JSON.stringify(opts)
  });
  return handleResponse(res);
}

export async function getRestSession(id) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/rest/${id}`, { headers });
  return handleResponse(res);
}

export async function markRestPlayed(id) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/rest/${id}/played`, { method: 'POST', headers });
  return handleResponse(res);
}

export async function keepRestSession(id, keep) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/rest/${id}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ keep })
  });
  return handleResponse(res);
}

export async function updateRestPreferences(prefs) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/rest/preferences`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(prefs)
  });
  return handleResponse(res);
}

export async function submitRestFeedback(id, feedback) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/rest/${id}/feedback`, {
    method: 'POST',
    headers,
    body: JSON.stringify(feedback)
  });
  return handleResponse(res);
}

// Web push (migration 040)

export async function getPushVapidKey() {
  // Public routes live under /prod/public/{proxy+} (no authorizer), not /api.
  const res = await fetch(`${API_URL.replace(/\/api$/, '/public')}/push/vapid-key`);
  return res.ok ? res.json() : { error: 'unavailable' };
}

export async function subscribePush(subscription) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/push/subscribe`, { method: 'POST', headers, body: JSON.stringify({ subscription }) });
  return handleResponse(res);
}

export async function unsubscribePush(endpoint) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/push/subscribe`, { method: 'DELETE', headers, body: JSON.stringify({ endpoint }) });
  return handleResponse(res);
}

export async function getPushSubscriptions() {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/push/subscriptions`, { headers });
  return handleResponse(res);
}

export async function sendTestPush() {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/push/test`, { method: 'POST', headers });
  return handleResponse(res);
}

export async function updateHints(body) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/user/hints`, { method: 'PATCH', headers, body: JSON.stringify(body) });
  return handleResponse(res);
}
